import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  HStack,
  Container
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillDollarCircle } from 'react-icons/ai';
import { FaAddressCard } from 'react-icons/fa';
import { IoMdShare } from 'react-icons/io';
import { MdAccountBox } from 'react-icons/md';
import { RiUser3Fill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { bankWithdraw } from '../../../api/postApi';
import CustomInput from '../../../components/InputComponents/customInput';
import useCurrencySymbol from '../../../hook/useCurrencySymbol';
import useCurrentWager from '../../../hook/useCurrentWager';
import GetParams from '../../../utils/getParams';
import { waitTime } from '../../../utils/waitTime';
import WithdrawNotice from './withdrawNotice';
import WithdrawPasswordInput from './withdrawPasswordInput';
import useMyBalance from '@/hook/useMyBalance';

const BankWithdrawV2 = ({ washConfig, selectedCurrency }) => {
  const { t } = useTranslation();
  const i18n = (key, props) => t(`settings.${key}`, { ...props });
  const i18n_deposit = (key, props) => t(`deposit.${key}`, { ...props });

  // UI Theme Hooks
  const textColor = useColorModeValue('dark.100', 'light.100');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const boxBg = useColorModeValue('light.100', 'dark.100');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const inputBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux Data
  const memberData = useSelector((state) => state.isMemberInfo);
  const currency_code = useSelector((state) => state.isWalletCurrency);
  const { bankAccount, withdraw_password_set } = memberData || {};

  const currentBankAccount = useMemo(() => {
    return bankAccount?.[selectedCurrency];
  }, [selectedCurrency, bankAccount]);

  // Derived Data
  const currentCurrencySymbol = useCurrencySymbol({
    origin: true,
    custom_currency: selectedCurrency
  });
  const { can_withdraw } = useCurrentWager() || {};
  const currency = GetParams('currency');
  const currency_balance = useMyBalance();

  // State
  const [withdrawPassword, setWithdrawPassword] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [buttonRendering, setButtonRendering] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [data, setData] = useState({
    bank_name: '',
    bank_branch_name: '',
    bank_card_number: '',
    bank_account: '',
    amount: 0,
    secret_pwd: '',
    understand: false
  });

  const {
    amount,
    secret_pwd,
    understand,
    bank_account
  } = data || {};

  // Handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    await bankWithdraw({
      currency_code: currency || currency_code,
      withdrawal_amount: amount,
      bank_account_number: bank_account,
      withdrawal_code: secret_pwd
    })
      .then((data) => {
        toast({
          title: i18n('withdrawalSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true
        });
        dispatch({ type: 'GET_BALANCE' });
        navigate('withdraw-success');
      })
      .catch((err) => {})
      .finally(() => {
        setButtonLoading(false);
      });
  };

  // Form Configurations
  const form = useMemo(() => {
    if (!currentBankAccount) return [];
    return [
      {
        label: i18n('BankName'),
        value: currentBankAccount.bank_name,
        icon: RiUser3Fill
      },
      {
        label: i18n('BankCard'),
        value: currentBankAccount.bank_card_number,
        icon: FaAddressCard
      },
      {
        label: i18n('BankBranch'),
        value: currentBankAccount.bank_branch_name,
        icon: IoMdShare
      },
      {
        label: i18n('BankAccount'),
        value: currentBankAccount.bank_account,
        icon: MdAccountBox
      }
    ];
  }, [selectedCurrency, currentBankAccount, i18n]);

  const amountInput = useMemo(
    () => ({
      label: (
        <Text fontSize="sm" fontWeight="bold" color={labelColor}>
          {i18n('amount')}({currentCurrencySymbol})
        </Text>
      ),
      name: 'amount',
      type: 'number',
      min: 10,
      icon: <Icon as={AiFillDollarCircle} color="gray.400" />,
      onChange: (value) => {
        setData((prev) => ({ ...prev, amount: value }));
      }
    }),
    [currentCurrencySymbol, i18n, labelColor]
  );

  // Effects
  useEffect(() => {
    const fillData = async () => {
        if (currentBankAccount) {
             const { is_crypto, ...rest } = currentBankAccount; 
             setApiLoading(true);
             setData(prev => ({ ...prev, ...rest }));
             await waitTime(1000); 
             setApiLoading(false);
        }
    };
    fillData();
  }, [selectedCurrency, currentBankAccount]);

  useEffect(() => {
    const buttonCalling = async () => {
      setButtonRendering(true);
      await waitTime(2500);
      setButtonRendering(false);
    };
    buttonCalling();
  }, []);

  return (
    <Container maxW="container.md" p={0}>
        <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
                
                {/* Bank Info Section */}
                <Box bg={boxBg} p={5} borderRadius="xl" shadow="sm" border="1px" borderColor={borderColor}>
                    <HStack justifyContent="space-between" mb={4}>
                         <HStack>
                            <Tag size="lg" colorScheme="brand" variant="solid" borderRadius="lg">
                                <Icon as={MdAccountBox} mr={2} />
                                {i18n('BankCardTitle')}
                            </Tag>
                         </HStack>
                         <Text fontSize="sm" color={labelColor} fontWeight="bold">{selectedCurrency}</Text>
                    </HStack>
                    
                    <VStack spacing={3} align="stretch">
                        {form.map((item, index) => (
                            <Flex key={index} justifyContent="space-between" alignItems="center" p={3} bg={inputBg} borderRadius="md">
                                <HStack>
                                    <Icon as={item.icon} color="brand.500" boxSize={5} />
                                    <Text color={labelColor} fontWeight="medium" fontSize="sm">{item.label}</Text>
                                </HStack>
                                <Text color={textColor} fontWeight="bold">{item.value}</Text>
                            </Flex>
                        ))}
                    </VStack>
                </Box>

                {/* Amount Section */}
                <Box bg={boxBg} p={5} borderRadius="xl" shadow="sm" border="1px" borderColor={borderColor}>
                     <Skeleton isLoaded={!apiLoading}>
                        <CustomInput props={amountInput} />
                     </Skeleton>
                     <Flex justifyContent="flex-end" mt={2} alignItems="center">
                        <Text fontSize="sm" color={labelColor} mr={2}>{i18n('current_currency_balance', { currency })}:</Text>
                        <Text as="span" color="brand.500" fontWeight="extrabold" fontSize="lg">{currency_balance}</Text>
                     </Flex>
                </Box>

                {/* Password Section */}
                <Box bg={boxBg} p={5} borderRadius="xl" shadow="sm" border="1px" borderColor={borderColor}>
                    <Text mb={3} fontSize="sm" fontWeight="bold" color={labelColor}>
                        {i18n('withdraw_password_label', { defaultValue: 'Withdraw Password' })} 
                    </Text>
                     <WithdrawPasswordInput
                        value={withdrawPassword}
                        onChange={(value) => {
                            setWithdrawPassword(value);
                            setData((prev) => ({ ...prev, secret_pwd: value }));
                        }}
                        withdraw_password_set={withdraw_password_set}
                    />
                </Box>

                {/* Notices & Terms */}
                <Box>
                    <WithdrawNotice selectedCurrency={selectedCurrency} />
                </Box>
                
                <Box px={2}>
                    <Checkbox
                        colorScheme="brand"
                        onChange={(e) => setData((prev) => ({ ...prev, understand: e.target.checked }))}
                        size="lg"
                    >
                        <Text fontSize="md" color={textColor} fontWeight="medium">{i18n_deposit('iUnderstand')}</Text>
                    </Checkbox>
                </Box>

                {/* Submit Button */}
                <Skeleton isLoaded={!buttonRendering}>
                    <Button
                        w="100%"
                        type="submit"
                        colorScheme="brand"
                        variant="solid" 
                        size="lg"
                        height="60px"
                        fontSize="xl"
                        isLoading={buttonLoading}
                        isDisabled={!amount || !secret_pwd || buttonLoading || !understand || !can_withdraw}
                        boxShadow="lg"
                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                        transition="all 0.2s"
                    >
                        {i18n('Submit')}
                    </Button>
                </Skeleton>

            </VStack>
        </form>
    </Container>
  );
};

export default BankWithdrawV2;
